/**
 * WebhookConfig Component
 *
 * Allows tenants to configure webhook URL for CRM integration.
 * Includes test button and delivery logs display.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  ExternalLink,
  Loader2,
  Play,
  Save,
  XCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';

interface WebhookLog {
  id: string;
  success: boolean;
  statusCode?: number;
  errorMessage?: string;
  attemptNumber: number;
  responseTimeMs?: number;
  createdAt: string;
  leadId?: string;
}

interface WebhookConfigProps {
  tenantId: string;
  webhookUrl: string;
  webhookEnabled: boolean;
  onUpdate: () => void;
}

export function WebhookConfig({
  tenantId,
  webhookUrl: initialUrl,
  webhookEnabled: initialEnabled,
  onUpdate,
}: WebhookConfigProps) {
  const { getToken } = useAuth();
  const [url, setUrl] = useState(initialUrl);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    statusCode?: number;
  } | null>(null);
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    setHasChanges(url !== initialUrl || enabled !== initialEnabled);
  }, [url, enabled, initialUrl, initialEnabled]);

  const loadWebhookLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/webhooks/logs?tenantId=${tenantId}&limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load webhook logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // Load webhook logs on mount
  useEffect(() => {
    loadWebhookLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Update via PostgreSQL API
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/tenants/${tenantId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          webhookUrl: url || null,
          webhookEnabled: enabled,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save webhook config');
      }

      onUpdate();
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save webhook config:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    if (!url) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/webhooks/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          webhookUrl: url,
          tenantId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.data) {
        setTestResult({
          success: data.data.success,
          message: data.data.success
            ? `Webhook responded with status ${data.data.statusCode}`
            : data.data.error || 'Webhook test failed',
          statusCode: data.data.statusCode,
        });
      } else {
        setTestResult({
          success: false,
          message: data.error || 'Failed to test webhook',
        });
      }

      // Refresh logs after test
      loadWebhookLogs();
    } catch (error) {
      console.error('Webhook test error:', error);
      setTestResult({
        success: false,
        message: 'Failed to connect to test endpoint',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const isValidUrl = (urlString: string) => {
    if (!urlString) return true; // Empty is valid (webhook disabled)
    try {
      const parsedUrl = new URL(urlString);
      return parsedUrl.protocol === 'https:' || parsedUrl.protocol === 'http:';
    } catch {
      return false;
    }
  };

  const urlError = url && !isValidUrl(url) ? 'Please enter a valid URL' : null;

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label htmlFor="webhook-enabled">Enable Webhook</Label>
          <p className="text-sm text-muted-foreground">
            Automatically send lead data when new leads are created
          </p>
        </div>
        <Switch
          id="webhook-enabled"
          checked={enabled}
          onCheckedChange={setEnabled}
        />
      </div>

      <Separator />

      {/* Webhook URL Input */}
      <div className="space-y-2">
        <Label htmlFor="webhook-url">Webhook URL</Label>
        <div className="flex gap-2">
          <Input
            id="webhook-url"
            type="url"
            placeholder="https://your-crm.com/api/webhooks/renovation-leads"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className={cn(urlError && 'border-destructive')}
          />
          <Button
            variant="outline"
            onClick={handleTest}
            disabled={isTesting || !url || !!urlError}
          >
            {isTesting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Test
              </>
            )}
          </Button>
        </div>
        {urlError && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {urlError}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          We'll send a POST request with lead data in JSON format
        </p>
      </div>

      {/* Test Result */}
      {testResult && (
        <div
          className={cn(
            'flex items-center gap-2 rounded-lg p-3 text-sm',
            testResult.success
              ? 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-200'
              : 'bg-destructive/10 text-destructive'
          )}
        >
          {testResult.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <XCircle className="h-4 w-4" />
          )}
          <span>{testResult.message}</span>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving || !hasChanges || !!urlError}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Separator />

      {/* Webhook Logs */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium">Recent Deliveries</h3>
            <p className="text-sm text-muted-foreground">Last 10 webhook delivery attempts</p>
          </div>
          <Button variant="ghost" size="sm" onClick={loadWebhookLogs} disabled={isLoadingLogs}>
            {isLoadingLogs ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Refresh'
            )}
          </Button>
        </div>

        {isLoadingLogs ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-muted py-8 text-center">
            <Clock className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-2 text-sm text-muted-foreground">No webhook deliveries yet</p>
            <p className="text-xs text-muted-foreground">
              Logs will appear here when leads are created
            </p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Status</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="w-[80px]">Code</TableHead>
                  <TableHead className="w-[100px]">Response</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Badge
                        variant={log.success ? 'default' : 'destructive'}
                        className={cn(
                          log.success && 'bg-green-100 text-green-800 hover:bg-green-100'
                        )}
                      >
                        {log.success ? 'Delivered' : 'Failed'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(log.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {log.statusCode || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {log.responseTimeMs ? `${log.responseTimeMs}ms` : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {log.errorMessage || (log.attemptNumber > 1 ? `Attempt ${log.attemptNumber}` : '-')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Separator />

      {/* Payload Documentation */}
      <div className="space-y-2">
        <h3 className="font-medium">Webhook Payload</h3>
        <p className="text-sm text-muted-foreground">
          When a lead is created, we send a POST request with the following JSON payload:
        </p>
        <pre className="rounded-lg bg-muted p-4 text-xs overflow-x-auto">
{`{
  "event": "lead.created",
  "timestamp": "2024-01-15T10:30:00.000Z",
  "lead": {
    "id": "uuid",
    "clientName": "John Smith",
    "clientPhone": "+15551234567",
    "clientEmail": "john@example.com",
    "roomType": "kitchen",
    "projectTimeline": "30_days",
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "state": "NY",
      "zip": "10001",
      "country": "US"
    },
    "selections": { ... },
    "quoteMin": 15000,
    "quoteMax": 22000,
    "sourceUrl": "https://your-site.com",
    "createdAt": "2024-01-15T10:30:00.000Z"
  },
  "tenant": {
    "id": "uuid",
    "companyName": "Your Company",
    "slug": "your-company"
  }
}`}
        </pre>
      </div>
    </div>
  );
}
